import { FC, useCallback, useEffect } from 'react'
import { FileUploadInput } from '../../util/SettingsUtil'
import { FormFields } from '../types'
import { Stack, Typography } from '@dtx-company/design-system/src'
import { UseFormMethods } from 'react-hook-form-deprecated'
import { Yield } from '@dtx-company/shared-components/src/foundation/Icons/Bespoke/Yield'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useLinkImageEditModalState } from './LinkImageEditModal/hooks'
import { usePreviousValue } from '@dtx-company/true-common/src/hooks/usePreviousValue/usePreviousValue'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'

const exceedsMaxFileSize = (fileSize: number): boolean => {
  const MAX_FILE_SIZE = 10485760 // 10MB
  return fileSize > MAX_FILE_SIZE
}

enum MaskType {
  CIRCLE = 'circle',
  SQUARE = 'square',
  RECTANGLE = 'rectangle',
  IPHONEX = 'iphonex',
  FEATURED = 'featured',
  NO_IMAGE = 'noImage'
}

const MaskObj = {
  circle: {
    name: MaskType.CIRCLE,
    borderRadius: 300,
    width: undefined,
    height: undefined
  },
  square: {
    name: MaskType.SQUARE,
    borderRadius: 20,
    width: undefined,
    height: undefined
  },
  featured: {
    name: MaskType.FEATURED,
    borderRadius: 0,
    width: 400,
    height: 160
  },
  rectangle: {
    name: MaskType.RECTANGLE,
    borderRadius: 0,
    width: 400,
    height: 225
  },
  iphonex: {
    name: MaskType.IPHONEX,
    borderRadius: 0,
    width: 200,
    height: 400
  },
  noImage: {
    name: MaskType.NO_IMAGE,
    borderRadius: 0,
    width: 0,
    height: 0
  }
}

interface ImageEditProps {
  id: string
  imageUrl?: string
  replaceButtonCTA?: string
  deleteButtonCTA?: string
  defaultImage?: string
  setValue: UseFormMethods['setValue']
  active: boolean
  showLabel?: boolean
  setImageSizeWarning?: (val: boolean) => void
  maskType?: MaskType.CIRCLE | MaskType.SQUARE | MaskType.IPHONEX | MaskType.FEATURED
  /** Crop the image according to maskType without user input or triggering the image edit modal.
    Specially useful when we are automatically fetching the og image for a link */
  performAutomaticDefaultCrop?: boolean
  labelOverride?: string
}

export const ImageEdit: FC<ImageEditProps> = ({
  imageUrl,
  setValue,
  defaultImage,
  maskType = MaskType.SQUARE,
  performAutomaticDefaultCrop = false,
  labelOverride
}: ImageEditProps) => {
  const hasImage = imageUrl || defaultImage

  const {
    resultImageUrl,
    openImageEditModal,
    setOriginalImageUrl,
    AutomaticDefaultCropper,
    originalImageUrl,
    defaultCroppedImageUrl
  } = useLinkImageEditModalState()

  const handleStagingImage = useCallback(
    (e: File | Blob): void => {
      hasImage
        ? events.Flowpage_PageProfile_Clicked_Replace_Icon_Image()
        : events.Flowpage_PageProfile_Clicked_Add_Icon_Image()

      const stagedImage = e
      if (stagedImage) {
        if (exceedsMaxFileSize(stagedImage.size)) {
          sendErrorNotification('Image is too large to upload (size limit 10MB)')
        } else {
          openImageEditModal({
            sourceImageUrl: URL.createObjectURL(stagedImage),
            maskType: maskType === MaskType.FEATURED ? MaskType.RECTANGLE : maskType,
            hideMask: true,
            initialAspectRatio:
              maskType === MaskType.FEATURED ? MaskObj.featured.width / MaskObj.featured.height : 1
          })
        }
      }
    },
    [hasImage, maskType, openImageEditModal]
  )

  const prevMaskType = usePreviousValue(maskType)
  const prevImageUrl = usePreviousValue(imageUrl)

  useEffect(() => {
    if (resultImageUrl) {
      if (resultImageUrl !== prevImageUrl) {
        setValue(FormFields.IMAGE, resultImageUrl)
      }
    } else if (performAutomaticDefaultCrop && defaultCroppedImageUrl) {
      setValue(FormFields.IMAGE, defaultCroppedImageUrl)
    }
  }, [resultImageUrl, defaultCroppedImageUrl, performAutomaticDefaultCrop, prevImageUrl, setValue])

  useEffect(() => {
    // User changed maskType, then trigger to modal for re-cropping/re-centering
    if (prevMaskType && prevMaskType !== maskType) {
      openImageEditModal({
        sourceImageUrl: originalImageUrl,
        maskType: maskType === MaskType.FEATURED ? MaskType.RECTANGLE : maskType,
        hideMask: true,
        initialAspectRatio:
          maskType === MaskType.FEATURED ? MaskObj.featured.width / MaskObj.featured.height : 1
      })
    }
  }, [imageUrl, prevMaskType, maskType, openImageEditModal, originalImageUrl])

  useEffect(() => {
    // If this is the first time we are seeing imageUrl, save it as the original
    // image url
    if (!prevImageUrl && imageUrl) {
      setOriginalImageUrl(imageUrl)
    }
  }, [prevImageUrl, imageUrl, setOriginalImageUrl])

  return (
    <Stack justifyContent={'space-between'} gap={'8px'}>
      <FileUploadInput handleUpload={handleStagingImage} label={labelOverride} />
      {performAutomaticDefaultCrop && !defaultCroppedImageUrl && <AutomaticDefaultCropper />}
    </Stack>
  )
}

export const ImageSizeWarning: FC = () => {
  return (
    <Stack alignItems="center">
      <Yield height="60px" width="60px" color="#e1cb00" />
      <Typography variant="labelMedium">
        Warning: This photo is low resolution and may come out pixelated on larger devices
      </Typography>
    </Stack>
  )
}
